/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
import { Button, DatePicker, Descriptions, Select, Space, Table, Tag, Tooltip, Input } from 'antd';
import { driveTypeEnum } from 'utils/types';
import React, { useState, useEffect, useContext } from 'react';
import LocaleContext from 'locales';
import styled from 'styled-components';
import moment from 'moment';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import UserSearch from 'components/UserSearch';

const StyledTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  > .ant-tag {
    margin-right: 0px;
    max-width: 120px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    &:has(.anticon-close.ant-tag-close-icon) {
      position: relative;
      padding-right: 20px;
    }
    > .anticon-close.ant-tag-close-icon {
      position: absolute;
      right: 7px;
      top: 5px;
    }
  }
`;

const styles = {
  spaceBetween: { display: 'flex', justifyContent: 'space-between' },
};
/**
 * FC: 현재 선택한 Drive의 정보를 표시
 * @param {{driveName:string, driveType:'individual'| 'share' , driveSize:number, ...}} data 선택한 드라이브의 정보
 * @returns ReactNode
 */
export default function DriveDescription({ type, data, setContentsData, isCreate }) {
  // states
  const [driveData, setDriveData] = useState({});
  const [userSearchNamePath, setUserSearchNamePath] = useState(undefined); // UserSearch를 설정할 폼의 네임 패스
  const [ownerList, setOwnerList] = useState([]);
  const [adminList, setAdminList] = useState([]);
  const [permissionList, setPermissionList] = useState([]);
  const { locale } = useContext(LocaleContext);

  // variables
  const { driveName, driveType, driveSize } = driveData;

  useEffect(() => {
    if (data) {
      if (data.beforeContents) {
        setOwnerList(data.beforeContents.owners);
        setAdminList(data.beforeContents.admins);
        setPermissionList(data.beforeContents.permissions);
      } else {
        setOwnerList(data.owners);
        setAdminList(data.admins);
        setPermissionList(data.permissions);
      }

      if (type === 'approval.types.modify-drive-permission') {
        setContentsData({
          owners: data.owners,
          admins: data.admins,
          permissions: data.permissions,
        });
      }
      const settingData = data.afterDriveSize
        ? {
            ...data,
            driveSize: data.afterDriveSize,
            afterDriveSize: data.driveSize,
          }
        : data;
      setDriveData(settingData);
    }
  }, [data]);

  const columns = [
    {
      title: locale.resource['network-drive.column.username.with.id'],
      dataIndex: 'userName',
      render: (value, record) => `${record.userName || record.name}(${record.sAMAccountName})`,
    },
    {
      title: locale.resource['network-drive.column.department'],
      dataIndex: 'organization',
    },
    {
      title: locale.resource['common.permission'],
      dataIndex: 'permission',
      width: 100,
      render: (value, record, index) => (
        <Select
          defaultValue={value || 'writable'}
          style={{ width: '100%' }}
          disabled={
            !isCreate ||
            type === 'approval.types.delete-drive' ||
            type === 'approval.types.modify-drive-size'
          }
          options={[
            {
              label: locale.resource['permission.control.option.readWrite'],
              value: 'writable',
            },
            {
              label: locale.resource['permission.control.option.readOnly'],
              value: 'readonly',
            },
            {
              label: locale.resource['permission.control.option.invalid'],
              value: 'invalid',
            },
          ]}
          onChange={(v) => {
            permissionList[index].permission = v;
            setContentsData((prev) => ({
              ...prev,
              permissions: permissionList,
            }));
          }}
        />
      ),
    },
    {
      title: locale.resource['network-drive.column.expire.date'],
      dataIndex: 'expireDate',
      width: 120,
      render: (value, record, index) => (
        <DatePicker
          defaultValue={value && moment(value, 'YYYY-MM-DD')}
          disabled={
            !isCreate ||
            type === 'approval.types.delete-drive' ||
            type === 'approval.types.modify-drive-size'
          }
          onChange={(v) => {
            permissionList[index].expireDate = v.format('YYYY-MM-DD');
            setContentsData((prev) => ({
              ...prev,
              permissions: permissionList,
            }));
          }}
        />
      ),
    },
  ];

  // 사용자 선택되었을 때
  const onUserSearchSelected = (selectedUser) => {
    // 선택 취소면 아무것도 안한다.
    if (!selectedUser) {
      setUserSearchNamePath(undefined);
      return;
    }

    if (userSearchNamePath === 'owners') {
      if (ownerList.some((owner) => owner.sAMAccountName === selectedUser.sAMAccountName)) {
        setUserSearchNamePath(undefined);
        return;
      }
      const ownersData = ownerList.filter(
        (owner) => owner.sAMAccountName !== selectedUser.sAMAccountName,
      );

      setOwnerList([...ownersData, selectedUser]);
      setContentsData((prev) => ({ ...prev, owners: [...ownersData, selectedUser] }));
    }
    if (userSearchNamePath === 'admins') {
      if (adminList.some((admin) => admin.sAMAccountName === selectedUser.sAMAccountName)) {
        setUserSearchNamePath(undefined);
        return;
      }
      const adminsData = adminList.filter(
        (admin) => admin.sAMAccountName !== selectedUser.sAMAccountName,
      );
      setAdminList([...adminsData, selectedUser]);
      setContentsData((prev) => ({ ...prev, admins: [...adminsData, selectedUser] }));
    }
    if (userSearchNamePath === 'permissions') {
      if (
        permissionList.some(
          (permission) => permission.sAMAccountName === selectedUser.sAMAccountName,
        )
      ) {
        setUserSearchNamePath(undefined);
        return;
      }
      const permissionData = permissionList.filter(
        (permission) => permission.sAMAccountName !== selectedUser.sAMAccountName,
      );
      setPermissionList([...permissionData, { ...selectedUser, permission: 'writable' }]);
      setContentsData((prev) => ({
        ...prev,
        permissions: [...permissionData, { ...selectedUser, permission: 'writable' }],
      }));
    }
    setUserSearchNamePath(undefined);
  };

  return Object.keys(driveData).length ? (
    <>
      <UserSearch visible={userSearchNamePath} onSelected={onUserSearchSelected} />
      <Descriptions column={1} bordered size="middle" labelStyle={{ width: 120 }}>
        <Descriptions.Item label={locale.resource['network-drive.column.name']}>
          {driveName}
        </Descriptions.Item>
        <Descriptions.Item label={locale.resource['txt.type']}>
          {driveTypeEnum[driveType]}
        </Descriptions.Item>
        <Descriptions.Item
          label={locale.resource['network-drive.label.capacity']}
        >{`${driveSize} GB`}</Descriptions.Item>
        <Descriptions.Item
          label={
            <Space style={styles.spaceBetween}>
              <div>{locale.resource['network-drive.form.owner']}</div>
              {isCreate &&
                type !== 'approval.types.delete-drive' &&
                type !== 'approval.types.modify-drive-size' && (
                  <Tooltip title={locale.resource['txt.create']}>
                    <Button
                      type="link"
                      icon={<AiOutlinePlusCircle className="anticon" />}
                      onClick={() => setUserSearchNamePath('owners')}
                    />
                  </Tooltip>
                )}
            </Space>
          }
        >
          <StyledTags>
            {ownerList.map((el) => (
              <Tag
                key={el.sAMAccountName}
                color="cyan"
                closable={
                  isCreate &&
                  type !== 'approval.types.delete-drive' &&
                  type !== 'approval.types.modify-drive-size'
                }
                onClose={(e) => {
                  setOwnerList(
                    ownerList.filter((owner) => owner.sAMAccountName !== el.sAMAccountName),
                  );
                  setContentsData((prev) => ({
                    ...prev,
                    owners: prev.owners.filter((v) => v.sAMAccountName !== el.sAMAccountName),
                  }));
                }}
              >
                {el.userName || el.name}
              </Tag>
            ))}
          </StyledTags>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <Space style={styles.spaceBetween}>
              <div>{locale.resource['txt.admin']}</div>
              {isCreate &&
                type !== 'approval.types.delete-drive' &&
                type !== 'approval.types.modify-drive-size' && (
                  <Tooltip title={locale.resource['txt.create']}>
                    <Button
                      type="link"
                      icon={<AiOutlinePlusCircle className="anticon" />}
                      onClick={() => setUserSearchNamePath('admins')}
                    />
                  </Tooltip>
                )}
            </Space>
          }
        >
          <StyledTags>
            {adminList.map((el) => (
              <Tag
                key={el.sAMAccountName}
                color="orange"
                closable={
                  isCreate &&
                  type !== 'approval.types.delete-drive' &&
                  type !== 'approval.types.modify-drive-size'
                }
                onClose={(e) => {
                  setAdminList(
                    adminList.filter((admin) => admin.sAMAccountName !== el.sAMAccountName),
                  );
                  setContentsData((prev) => ({
                    ...prev,
                    admins: prev.admins.filter((v) => v.sAMAccountName !== el.sAMAccountName),
                  }));
                }}
              >
                {el.userName || el.name}
              </Tag>
            ))}
          </StyledTags>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <Space style={styles.spaceBetween}>
              <div>{locale.resource['common.permission']}</div>
              {isCreate &&
                type !== 'approval.types.delete-drive' &&
                type !== 'approval.types.modify-drive-size' && (
                  <Tooltip title={locale.resource['txt.create']}>
                    <Button
                      type="link"
                      icon={<AiOutlinePlusCircle className="anticon" />}
                      onClick={() => setUserSearchNamePath('permissions')}
                    />
                  </Tooltip>
                )}
            </Space>
          }
        >
          <Table
            size="small"
            pagination={false}
            rowKey="sAMAccountName"
            columns={
              isCreate
                ? [
                    ...columns,
                    {
                      title: locale.resource['txt.action'],
                      width: 80,
                      align: 'right',
                      render: (text, record, index) =>
                        isCreate &&
                        type !== 'approval.types.delete-drive' &&
                        type !== 'approval.types.modify-drive-size' && (
                          <Space size="middle">
                            <Button
                              type="link"
                              style={{ padding: 0 }}
                              onClick={() => {
                                const permissionData = permissionList.filter(
                                  (permission) =>
                                    permission.sAMAccountName !== record.sAMAccountName,
                                );
                                setPermissionList(permissionData);
                                setContentsData((prev) => ({
                                  ...prev,
                                  permissions: prev.permissions.filter(
                                    (permission) =>
                                      permission.sAMAccountName !== record.sAMAccountName,
                                  ),
                                }));
                              }}
                            >
                              {locale.resource['txt.delete']}
                            </Button>
                          </Space>
                        ),
                    },
                  ]
                : columns
            }
            dataSource={permissionList}
          />
        </Descriptions.Item>
      </Descriptions>
    </>
  ) : null;
}
